// src/components/lessons/renderers/ExamSectionRenderer.jsx
import React from 'react';

// Import other renderers for nested blocks
import TipListRenderer from './TipListRenderer';
import StepByStepRenderer from './StepByStepRenderer';
import TipRenderer from './TipRenderer';
import WarningRenderer from './WarningRenderer';
import ExampleRenderer from './ExampleRenderer';
import TipsGridRenderer from './TipsGridRenderer';
import OutlineRenderer from './OutlineRenderer';
import ParagraphRenderer from './ParagraphRenderer';

const ExamSectionRenderer = ({ block, isMobile }) => {
  // Process text with bold markdown (**text**)
  const processText = (text) => {
    if (typeof text !== 'string') return text;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) => {
      return index % 2 === 1 ? (
        <strong key={index} style={{
          fontWeight: '700',
          color: '#000000',
          background: '#FFFFFF',
          padding: '0.125rem 0.375rem',
          borderRadius: '0.25rem',
          border: '1px solid #E5E7EB'
        }}>
          {part}
        </strong>
      ) : part;
    });
  };
  // Function to render nested blocks
  const renderBlock = (nestedBlock, index) => {
    const key = `${nestedBlock.type}-${index}`;

    try {
      switch (nestedBlock.type) {
        case 'tip_list':
          return <TipListRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'step_by_step':
          return <StepByStepRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'tip':
          return <TipRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'warning':
          return <WarningRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'example':
          return <ExampleRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'tips_grid':
          return <TipsGridRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'outline':
          return <OutlineRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'paragraph':
          return <ParagraphRenderer key={key} block={nestedBlock} isMobile={isMobile} />;
        case 'redemittel':
          // Special handling for redemittel - render as a list
          return (
            <div key={key} style={{
              background: '#000000',
              padding: isMobile ? '1rem' : '1.25rem',
              borderRadius: '0.5rem',
              marginBottom: '1rem'
            }}>
              <p style={{
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                color: '#FFFFFF',
                margin: 0,
                marginBottom: '0.75rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#3A86FF'
                }} />
                {nestedBlock.title || 'Redemittel'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {Array.isArray(nestedBlock.redemittel) ? (
                  nestedBlock.redemittel.map((item, i) => (
                    <p key={i} style={{
                      fontSize: isMobile ? '0.9rem' : '0.95rem',
                      color: '#FFFFFF',
                      margin: 0,
                      lineHeight: '1.6',
                      opacity: 0.95
                    }}>
                      {processText(item)}
                    </p>
                  ))
                ) : (
                  <p style={{
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    color: '#FFFFFF',
                    margin: 0,
                    lineHeight: '1.6',
                    opacity: 0.95
                  }}>
                    {processText(nestedBlock.redemittel)}
                  </p>
                )}
              </div>
            </div>
          );
        default:
          return null;
      }
    } catch (error) {
      console.error(`Error rendering nested block type "${nestedBlock.type}":`, error);
      return null;
    }
  };

  return (
    <div style={{
      background: '#FFFFFF',
      border: '2px solid #000000',
      borderRadius: isMobile ? '0.5rem' : '0.75rem',
      padding: isMobile ? '0.875rem 1rem' : '1.75rem',
      marginBottom: isMobile ? '1.25rem' : '2rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header Section with Title and Duration */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: isMobile ? '0.75rem' : '1rem',
        paddingBottom: isMobile ? '0.75rem' : '1rem',
        borderBottom: '2px solid #E5E7EB'
      }}>
        <h3 style={{
          fontSize: isMobile ? '1.0625rem' : '1.5rem',
          fontWeight: '700',
          color: '#000000',
          marginBottom: isMobile ? '0.5rem' : 0
        }}>
          {block.title}
        </h3>

        {block.duration && (
          <div style={{
            background: '#3A86FF',
            color: '#FFFFFF',
            padding: '0.5rem 1rem',
            borderRadius: '2rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}>
            ⏱️ {block.duration}
          </div>
        )}
      </div>

      {/* Description */}
      {block.description && (
        <p style={{
          color: '#000000',
          marginBottom: isMobile ? '1rem' : '1.5rem',
          lineHeight: isMobile ? '1.65' : '1.7',
          fontSize: isMobile ? '0.9375rem' : '1rem',
          opacity: 0.85
        }}>
          {processText(block.description)}
        </p>
      )}

     {/* Sub Parts */}
{block.sub_parts && block.sub_parts.length > 0 && (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    {block.sub_parts.map((subPart, index) => {
      // If sub_part has a 'type' field, render it using the appropriate renderer
      if (subPart.type) {
        return renderBlock(subPart, index);
      }

      // Otherwise, render as a regular sub-part with title and task
      return (
        <div
          key={index}
          style={{
            background: '#FAFBFC',
            border: '1px solid #E5E7EB',
      borderRadius: isMobile ? '0.375rem' : '0.5rem',
      padding: isMobile ? '0.75rem 1rem' : '1.25rem',
      transition: 'all 0.2s ease',
      cursor: 'default'
    }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#3A86FF';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(58, 134, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#E5E7EB';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {/* Sub Part Title */}
          {subPart.title && (
            <h4 style={{
              fontSize: isMobile ? '0.875rem' : '1.05rem',
              fontWeight: '700',
              color: '#000000',
              marginBottom: isMobile ? '0.375rem' : '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0.375rem' : '0.5rem'
            }}>
              <span style={{
                background: '#000000',
                color: '#FFFFFF',
                width: isMobile ? '22px' : '24px',
                height: isMobile ? '22px' : '24px',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '0.6875rem' : '0.75rem',
                fontWeight: '700',
                flexShrink: 0
              }}>
                {index + 1}
              </span>
              {subPart.title}
            </h4>
          )}

          {/* Sub Part Task */}
          {subPart.task && (
            <p style={{
              color: '#000000',
              lineHeight: '1.6',
              fontSize: isMobile ? '0.875rem' : '0.95rem',
              opacity: 0.8,
              marginLeft: subPart.title ? '2rem' : 0
            }}>
              {processText(subPart.task)}
            </p>
          )}
        </div>
      );
    })}
  </div>
)}
    </div>
  );
};

export default ExamSectionRenderer;